import { Card, Alert, Loader } from '@dreamtree-org/twreact-ui';

/**
 * The Runs tab's list + step timeline. Lifted out of WorkflowDetail unchanged when that page
 * became the builder shell.
 *
 * A step's output is collapsed by default: payloads are truncated server-side at 60k, which
 * is still far more than belongs in a timeline row.
 */

const RUN_TONE = {
  done: 'text-emerald-700', error: 'text-red-700', running: 'text-amber-700', queued: 'text-secondary-500',
};
/** Step timeline rail colour, keyed rather than nested-ternaried. */
const STEP_RAIL = { ok: '#16a34a', error: '#dc2626' };
const stepRailColor = (status) => STEP_RAIL[status] || '#d97706';

function StepRow({ step }) {
  return (
    <li className="border-l-2 pl-3" style={{ borderColor: stepRailColor(step.status) }}>
      <div className="flex justify-between text-sm">
        <span className="font-mono">
          {step.seq}. {step.node_id} <span className="text-secondary-500">({step.node_type})</span>
        </span>
        <span className="text-xs">
          {step.branch_taken ? `→ ${step.branch_taken}` : ''} {step.duration_ms != null ? `${step.duration_ms}ms` : ''}
        </span>
      </div>
      {step.error && <div className="text-xs text-red-700">{step.error}</div>}
      {step.output_json != null && (
        <details>
          <summary className="text-xs text-secondary-500 cursor-pointer">output</summary>
          <pre className="text-xs overflow-x-auto bg-[var(--app-surface)] p-2 rounded mt-1">
            {JSON.stringify(step.output_json, null, 2)}
          </pre>
        </details>
      )}
    </li>
  );
}

export default function RunsPanel({ runs, selectedRun, steps, onSelectRun, loading }) {
  return (
    <div className="grid grid-cols-3 gap-2 h-full">
      <Card className="p-3 overflow-y-auto">
        <div className="font-semibold text-sm mb-2">Runs</div>
        {loading && !runs.length ? <Loader text="Loading runs…" /> : null}
        {!loading && !runs.length ? <p className="text-sm text-secondary-500">No runs yet.</p> : null}
        <ul className="space-y-1">
          {runs.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelectRun(r.id)}
                className={`w-full text-left px-2 py-1 rounded text-sm ${selectedRun?.id === r.id ? 'bg-primary-50 border border-primary-200' : 'hover:bg-[var(--app-surface)]'}`}
              >
                <div className="flex justify-between gap-2">
                  <span>#{r.id}</span>
                  <span className={RUN_TONE[r.status] || ''}>{r.status}</span>
                </div>
                <div className="text-xs text-secondary-500">
                  {r.trigger} · {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-3 col-span-2 overflow-y-auto">
        {!selectedRun ? <p className="text-sm text-secondary-500">Select a run to see its step timeline.</p> : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm">Run #{selectedRun.id}</div>
              <span className={RUN_TONE[selectedRun.status] || ''}>{selectedRun.status}</span>
            </div>
            {selectedRun.error && <Alert variant="error">{selectedRun.error}</Alert>}
            <ol className="space-y-2">
              {steps.map((s) => <StepRow key={s.id} step={s} />)}
            </ol>
            {selectedRun.output_json != null && (
              <div>
                <div className="text-sm font-semibold">Result</div>
                <pre className="text-xs overflow-x-auto bg-[var(--app-surface)] p-2 rounded">
                  {JSON.stringify(selectedRun.output_json, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
